import { useState } from "react";
import { motion } from "framer-motion";
import {
  PlayCircle,
  CheckSquare,
  Banknote,
  CreditCard,
  Upload,
  LayoutDashboard,
  HelpCircle,
  ArrowUpRight,
  Calendar,
  Truck,
  ChevronDown,
} from "lucide-react";
import { LINKS } from "@/config/links";

const VIDEOS = [
  { key: "qbo-invite", icon: CheckSquare, title: "Accept your QuickBooks Online invitation", desc: "Walkthrough for accepting the QBO accountant invite and granting access.", src: LINKS.loomQboInvite },
  { key: "bank-feeds", icon: Banknote, title: "Connect your bank feeds", desc: "Securely link your business checking and savings to QuickBooks Online.", src: LINKS.loomBankFeeds },
  { key: "credit-cards", icon: CreditCard, title: "Connect your credit cards", desc: "Add business credit cards so transactions flow into QBO automatically.", src: LINKS.loomCreditCards },
  { key: "upload-docs", icon: Upload, title: "Upload documents to the portal", desc: "Where to drop statements, receipts, and tax docs in Financial Cents.", src: LINKS.loomUploadDocs },
  { key: "portal", icon: LayoutDashboard, title: "Using your Client Portal", desc: "Messages, tasks, deliverables — a tour of Financial Cents.", src: LINKS.loomPortal },
  { key: "faqs", icon: HelpCircle, title: "FAQs & quick answers", desc: "The questions we get most often, answered in a few minutes.", src: LINKS.loomFaqs },
];

const FAQS = [
  { q: "How quickly will you respond when I send something?", a: "Async messages are responded to within one business day. Urgent items can always be scheduled via Calendly." },
  { q: "Do I need QuickBooks Online?", a: "Yes — QBO is our system of record. We help you get set up, accept the accountant invite, and connect your feeds during onboarding." },
  { q: "How do I send receipts and statements?", a: "Drop them in the Client Portal (Financial Cents). You can upload from desktop or mobile — we'll organize from there." },
  { q: "When do you handle IFTA?", a: "Quarterly — we'll collect mileage and fuel records, prepare the return, and route to you for filing and payment." },
];

export default function Onboarding() {
  return (
    <div data-testid="page-onboarding" className="min-h-screen bg-[#040914] text-white">
      {/* Bare hidden-hub header (no main nav) */}
      <header className="border-b border-white/5 bg-[#040914]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl bg-[#00274C] border border-white/10 grid place-items-center">
              <Truck className="h-4.5 w-4.5 text-[#FFCB05]" strokeWidth={2.25} />
            </span>
            <div>
              <p className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">Onboarding Hub</p>
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">Linehaul Books · Client Resources</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <a
              href={LINKS.qbo}
              target="_blank"
              rel="noreferrer"
              data-testid="onboarding-connect-qbo-btn"
              className="px-4 py-2 rounded-full text-sm font-medium border border-white/15 text-white hover:border-[#FFCB05]/40 hover:bg-white/5 transition-colors"
            >
              Connect QuickBooks
            </a>
            <a
              href={LINKS.calendly}
              target="_blank"
              rel="noreferrer"
              data-testid="onboarding-book-call-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-colors"
            >
              Book Onboarding Call <Calendar className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-12 sm:pt-20 sm:pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Welcome aboard</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight text-white leading-[1.05]">
              Everything you need to <span className="text-[#FFCB05]">get rolling</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Short, focused videos to walk you through onboarding — at your pace. Watch in any order. Bookmark this page and come back any time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section className="relative pb-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="p-6 sm:p-8 rounded-2xl border border-white/5 bg-[#0A111E]">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Quick checklist</p>
            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                "Accept the QuickBooks Online invitation we sent",
                "Connect your bank feeds in QBO",
                "Connect your credit cards in QBO",
                "Log into the Client Portal (Financial Cents)",
                "Upload your last 3 months of statements",
                "Book your kickoff call via Calendly",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[#9CA3AF]">
                  <CheckSquare className="h-4 w-4 text-[#FFCB05] mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* VIDEO GRID */}
      <section className="relative py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Walkthroughs</p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">Loom videos · approx 2–5 min each</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VIDEOS.map((v, i) => (
              <motion.div
                key={v.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group p-6 rounded-2xl bg-gradient-to-b from-[#00274C]/45 to-[#040914] border border-white/5 hover:border-[#FFCB05]/30 transition-all"
                data-testid={`onboarding-video-${v.key}`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-[#040914] border border-white/10 group-hover:border-[#FFCB05]/40 transition-colors">
                    <v.icon className="h-5 w-5 text-[#FFCB05]" />
                  </span>
                  <h3 className="font-display text-lg font-semibold text-white">{v.title}</h3>
                </div>
                <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{v.desc}</p>

                <div className="mt-5 relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-[#040914]">
                  <iframe
                    title={v.title}
                    src={v.src}
                    frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                  {/* fallback overlay for placeholder URLs */}
                  <div className="pointer-events-none absolute inset-0 grid place-items-center bg-[#040914]/60">
                    <div className="text-center px-6">
                      <PlayCircle className="h-12 w-12 text-[#FFCB05] mx-auto" strokeWidth={1.5} />
                      <p className="mt-2 text-xs text-[#9CA3AF]">Loom video placeholder · replace with your share URL</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section className="relative py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">FAQs</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">Quick answers.</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((f, i) => <Faq key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="relative py-12 pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "QuickBooks Online", desc: "Accept the accountant invite", href: LINKS.qbo, testid: "onboarding-qbo-link" },
              { label: "Financial Cents Portal", desc: "Upload docs, see deliverables", href: LINKS.financialCents, testid: "onboarding-portal-link" },
              { label: "Calendly", desc: "Book onboarding & check-ins", href: LINKS.calendly, testid: "onboarding-calendly-link" },
            ].map((x) => (
              <a
                key={x.label}
                href={x.href}
                target="_blank"
                rel="noreferrer"
                data-testid={x.testid}
                className="group flex items-center justify-between p-6 rounded-2xl bg-[#0A111E] border border-white/5 hover:border-[#FFCB05]/30 transition-all"
              >
                <div>
                  <p className="font-display text-base font-semibold text-white">{x.label}</p>
                  <p className="text-sm text-[#9CA3AF] mt-0.5">{x.desc}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 text-[#9CA3AF] group-hover:text-[#FFCB05] transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Faq({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A111E]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        data-testid={`onboarding-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-medium text-white">{q}</span>
        <ChevronDown className={`h-4 w-4 text-[#FFCB05] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-[#9CA3AF] leading-relaxed">{a}</div>}
    </div>
  );
}
